import { Box, Stack, Typography } from "@mui/material"
import { useLocalization } from "@fluent/react"
import { useEffect, useState } from "react"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import FavoriteIcon from "@mui/icons-material/Favorite"

export type MeetingPulseType = "heart" | "rhythm" | "progress"

export type MeetingElapsedTimeProps = {
    startDate: string
    plannedMinutes: number | null
    showPulse?: boolean
    pulsType?: MeetingPulseType
    showRemainingMinutes?: boolean
}

// Ostatnia 1/3 czasu: np. ostatnie 20 minut przy spotkaniu na 60 minut.
const NEAR_END_RATIO = 1 / 3

const HEART_SETTINGS = {
    normal: {
        color: "success.main",
        duration: 1.5,
        peakScale: 1.2,
        secondScale: 1.1,
    },
    nearEnd: {
        color: "error.main",
        duration: 0.65,
        peakScale: 1.5,
        secondScale: 1.25,
    },
    timeUp: {
        color: "error.main",
        duration: 0,
        peakScale: 1,
        secondScale: 1,
    },
}

const RHYTHM_SETTINGS = {
    normal: { duration: 3, amplitude: 5, strokeWidth: 2 },
    nearEnd: { duration: 1, amplitude: 10, strokeWidth: 3 },
    timeUp: { duration: 0, amplitude: 0, strokeWidth: 4 },
}

const pad = (value: number) => String(value).padStart(2, "0")

const formatTime = (durationSeconds: number) =>
    `${pad(Math.floor(durationSeconds / 3600))}:${pad(
        Math.floor((durationSeconds % 3600) / 60)
    )}:${pad(durationSeconds % 60)}`

type MeetingIndicatorProps = {
    pulsType: MeetingPulseType
    status: keyof typeof HEART_SETTINGS
    progressPercent: number | null
    progressLabel: string
    timeLabel?: string
}

const MeetingIndicator = ({
    pulsType,
    status,
    progressPercent,
    progressLabel,
    timeLabel,
}: MeetingIndicatorProps) => {
    const heart = HEART_SETTINGS[status]
    const rhythm = RHYTHM_SETTINGS[status]
    const isTimeUp = status === "timeUp"

    if (pulsType === "heart") {
        return (
            <Box
                aria-hidden="true"
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: heart.color,
                    transition: "color 200ms ease",
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                }}
            >
                <FavoriteIcon
                    sx={{
                        fontSize: 20,
                        transformOrigin: "center",
                        transform: "scale(1)",
                        opacity: isTimeUp ? 0.45 : 1,
                        transition: "opacity 200ms ease",
                        "--heart-peak-scale": heart.peakScale,
                        "--heart-second-scale": heart.secondScale,
                        animation: isTimeUp
                            ? "none"
                            : `meetingHeartBeat ${heart.duration}s ease-in-out infinite`,
                        // Dwa uderzenia i krótka przerwa.
                        "@keyframes meetingHeartBeat": {
                            "0%, 40%, 100%": { transform: "scale(1)" },
                            "12%": { transform: "scale(var(--heart-peak-scale))" },
                            "22%": { transform: "scale(1)" },
                            "30%": { transform: "scale(var(--heart-second-scale))" },
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                            transition: "none",
                            transform: "none",
                        },
                    }}
                />
            </Box>
        )
    }

    if (pulsType === "rhythm") {
        // ViewBox mieści również większe wychylenia i grubość linii.
        const centerY = 14
        const smallPeak = rhythm.amplitude * 0.3
        const path = `
            M 2 ${centerY}
            H 18
            L 23 ${centerY - smallPeak}
            L 27 ${centerY + smallPeak}
            L 33 ${centerY - rhythm.amplitude}
            L 39 ${centerY + rhythm.amplitude}
            L 44 ${centerY}
            H 82
            L 87 ${centerY - smallPeak}
            L 91 ${centerY + smallPeak}
            L 97 ${centerY - rhythm.amplitude}
            L 103 ${centerY + rhythm.amplitude}
            L 108 ${centerY}
            H 126
        `

        return (
            <Box
                aria-hidden="true"
                sx={{
                    width: 72,
                    height: 32,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Box
                    component="svg"
                    viewBox="0 0 128 28"
                    preserveAspectRatio="none"
                    sx={{
                        width: "100%",
                        height: 22,
                        color: heart.color,
                        transition: "color 200ms ease",
                        "& path": {
                            animation: isTimeUp
                                ? "none"
                                : `meetingElapsedTrace ${rhythm.duration}s linear infinite`,
                            transition: "stroke-width 200ms ease",
                        },
                        "@keyframes meetingElapsedTrace": {
                            "0%": { strokeDasharray: "0 100", strokeDashoffset: 0 },
                            "38%, 62%": { strokeDasharray: "100 0", strokeDashoffset: 0 },
                            "100%": { strokeDasharray: "0 100", strokeDashoffset: -100 },
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                            "& path": { animation: "none", transition: "none" },
                        },
                    }}
                >
                    <path
                        d={path}
                        pathLength="100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={rhythm.strokeWidth}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                width: 72,
                height: 32,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
            }}
        >
            <Box
                role={progressPercent === null ? "img" : "progressbar"}
                aria-label={progressLabel}
                aria-valuemin={progressPercent === null ? undefined : 0}
                aria-valuemax={progressPercent === null ? undefined : 100}
                aria-valuenow={progressPercent === null
                    ? undefined
                    : Math.round(progressPercent * 10) / 10}
                aria-valuetext={timeLabel}
                title={timeLabel ?? progressLabel}
                sx={{
                    width: "100%",
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "action.hover",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        // Bez planu nie da się wyliczyć procentu — pasek jest pusty.
                        width: `${progressPercent ?? 0}%`,
                        height: "100%",
                        borderRadius: "inherit",
                        bgcolor: heart.color,
                        transition: "width 200ms linear, background-color 200ms ease",
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                        },
                    }}
                />
            </Box>
        </Box>
    )
}

export const MeetingElapsedTime = ({
    startDate,
    plannedMinutes,
    showPulse = true,
    pulsType = "heart",
    showRemainingMinutes = true,
}: MeetingElapsedTimeProps) => {
    const { l10n } = useLocalization()
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        setNow(Date.now())

        const interval = window.setInterval(() => {
            setNow(Date.now())
        }, 1000)

        return () => window.clearInterval(interval)
    }, [startDate])

    const startMs = Date.parse(startDate)

    if (!Number.isFinite(startMs)) return null

    const totalSeconds = Math.max(0, Math.floor((now - startMs) / 1000))
    const elapsed = formatTime(totalSeconds)

    const plannedSeconds =
        plannedMinutes !== null &&
        Number.isFinite(plannedMinutes) &&
        plannedMinutes > 0
            ? Math.max(1, Math.round(plannedMinutes * 60))
            : null

    const remainingSeconds =
        plannedSeconds === null ? null : plannedSeconds - totalSeconds

    const isTimeUp =
        remainingSeconds !== null && remainingSeconds <= 0

    const isNearEnd =
        plannedSeconds !== null &&
        remainingSeconds !== null &&
        remainingSeconds > 0 &&
        remainingSeconds <= plannedSeconds * NEAR_END_RATIO

    const status = isTimeUp ? "timeUp" : isNearEnd ? "nearEnd" : "normal"
    const accentColor = HEART_SETTINGS[status].color

    const progressPercent = plannedSeconds === null
        ? null
        : Math.min(100, Math.max(0, (totalSeconds / plannedSeconds) * 100))

    const progressLabel = plannedSeconds === null
        ? l10n.getString("meeting-timer-no-plan", null, "_Brak zaplanowanego czasu")
        : l10n.getString("meeting-timer-progress", null, "_Postęp spotkania")

    const minutes =
        remainingSeconds === null
            ? null
            : Math.ceil(Math.abs(remainingSeconds) / 60)

    const timeDescription =
        minutes === null
            ? null
            : `${remainingSeconds !== null && remainingSeconds < 0 ? "+" : ""}${minutes} min`

    const timeLabel =
        remainingSeconds === null
            ? undefined
            : remainingSeconds < 0
                ? l10n.getString(
                    "meeting-timer-overtime",
                    { time: formatTime(-remainingSeconds) },
                    `_Przekroczono o ${formatTime(-remainingSeconds)}`
                )
                : remainingSeconds === 0
                    ? l10n.getString(
                        "meeting-timer-time-up",
                        null,
                        "_Planowany czas upłynął"
                    )
                    : l10n.getString(
                        "meeting-timer-remaining",
                        { time: formatTime(remainingSeconds) },
                        `_Pozostało ${formatTime(remainingSeconds)}`
                    )

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ px: 0.5, color: "text.secondary" }}
        >
            <ScheduleOutlinedIcon sx={{ fontSize: 17 }} aria-hidden="true" />

            <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography
                    component="span"
                    variant="body2"
                    aria-label={`${l10n.getString(
                        "meeting-refresh-time",
                        null,
                        "_Czas spotkania"
                    )}: ${elapsed}`}
                    sx={{
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                    }}
                >
                    {elapsed}
                </Typography>

                {(showPulse || (showRemainingMinutes && timeDescription)) && (
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        {showPulse && (
                            <MeetingIndicator
                                pulsType={pulsType}
                                status={status}
                                progressPercent={progressPercent}
                                progressLabel={progressLabel}
                                timeLabel={timeLabel}
                            />
                        )}

                        {showRemainingMinutes && timeDescription && (
                            <Typography
                                component="span"
                                variant="caption"
                                color={accentColor}
                                aria-label={timeLabel}
                                sx={{
                                    fontVariantNumeric: "tabular-nums",
                                    whiteSpace: "nowrap",
                                    lineHeight: 1.2,
                                    transition: "color 200ms ease",
                                    "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                    },
                                }}
                            >
                                {timeDescription}
                            </Typography>
                        )}
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}
